/**
 * Třída obsahující metody pro práci datem.
 */
class Dates {
	/**
	 * Zjistí počet dní v měsíci.
	 * @param {number} month 0 = leden
	 * @param {number} year Rok.
	 * @return {number} Vrací počet dní v daném měsíci.
	 */
	static daysInMonth(month, year) {
		return new Date(year, month + 1, 0).getDate();
	}

	/**
	 * Převede string z DD.MM.YYYY na timestamp.
	 * @param {string} dateString String, který bude převeden na timestamp.
	 */
	static stringToTimestamp(dateString) {
		const dateParts = dateString.split('.');

		const date = new Date(Number(dateParts[2]), Number(dateParts[1]) - 1, Number(dateParts[0]));

		date.setHours(0);

		return date.getTime();
	}

	/**
	 * Převede timestamp na string ve tvaru DD.MM.YYYY.
	 * @param {number} timestamp Timestamp, který bude převeden na string.
	 * @return {string} Vrací datum ve formátu DD.MM.YYYY.
	 */
	static timestampToString(timestamp) {
		if (!timestamp) {
			return null;
		}

		const date = new Date(timestamp);

		return date.getDate() + '.' + (date.getMonth() + 1) + '.' + date.getFullYear();
	}

	/**
	 * Převede timestamp na string ve tvaru %Y-%m-d,
	 * který se používá pro API calls.
	 * @param {number} timestamp Timestamp, který bude převeden na string.
	 * @return {string}
	 */
	static timestampToAPIFormat(timestamp) {
		const date = new Date(timestamp);

		return date.getFullYear() + '-' + (date.getMonth() + 1) + '-' + date.getDate();
	}

	static stringToAPIFormat(string) {
		const timestamp = this.stringToTimestamp(string);

		return this.timestampToAPIFormat(timestamp);
	}

	static stringToFullAPIFormat(string) {
		return this.stringToAPIFormat(string) + 'T00:00';
	}

	/**
	 * Parsuje string s datem (a časem) a vrátí objekt Date pouze s datem (a půlnocí)
	 * @param {String} dateString
	 * @returns {Date}
	 */
	static stringToJustADate(dateString) {
		const dateTimeRegex = /^\s*(\d{1,2})\.\s*(\d{1,2})\.\s*(\d{2}\d{2}?)(\s+(\d{1,2})[\.:](\d{2}))?\s*$/; // example: 2. 12. 2019 9:20 / 09.02.2018 10.00 ...

		const m = dateString.match(dateTimeRegex);

		return m ? new Date(m[3], m[2] - 1, m[1]) : null;
	}

	static fullApiFormatToTimestamp(fullApiFormat) {
		const date = new Date(fullApiFormat);

		return date.getTime();
	}

	static fullApiFormatToString(fullApiFormat) {
		const timestamp = this.fullApiFormatToTimestamp(fullApiFormat);

		return this.timestampToString(timestamp);
	}

	/**
	 * Zjistí jakým dnem začíná daný měsíc.
	 * Pro vykreslování kalendáře je vhodnější, pokud getDay()
	 * vrací pro neděli = 6, namísto defaultní 0.
	 * @param {number} year Rok.
	 * @param {number} month 0 = leden
	 * @return {number} Vrací číslo prvního dne v měsící, pondělí = 0;
	 */
	static monthStartsAt(year, month) {
		const day = new Date(year, month).getDay();

		if (day === 0) {
			return 6;
		} else {
			return day - 1;
		}
	}

	/**
	 * Zjistí číslo roku daného otisku.
	 * @param {number} timestamp Timestamp, z kterého bude získán rok.
	 * @return {number} Vrací číslo roku daného otisku.
	 */
	static getYearFromTimestamp(timestamp) {
		return new Date(timestamp).getFullYear();
	}

	/**
	 * Zjistí číslo měsíce daného otisku.
	 * @param {number} timestamp Timestamp, z kterého bude získán měsíc.
	 * @return {number} Vrací číslo měsíce daného otisku.
	 */
	static getMonthFromTimestamp(timestamp) {
		return new Date(timestamp).getMonth();
	}

	/**
	 * Vrací předchozí měsíc/rok
	 * @param {number} month 0 = leden
	 * @param {number} year Rok.
	 * @return {object} Vrací {month, year}
	 */
	static dateMinusMonth(month, year) {
		if (month === 0) {
			return { month: 11, year: year - 1 };
		} else {
			return { month: month - 1, year };
		}
	}

	/**
	 * Vrací následující měsíc/rok
	 * @param {number} month 0 = leden
	 * @param {number} year Rok.
	 * @return {object} Vrací {month, year}
	 */
	static datePlusMonth(month, year) {
		if (month === 11) {
			return { month: 0, year: year + 1 };
		} else {
			return { month: month + 1, year };
		}
	}
	// eslint-disable-next-line custom-oxlint/jsdoc__multiline-blocks
	/** 15.9.2021
	 * @param {string} date
	 * @return {boolean}
	 */
	static isToday = (date) => {
		const [day, month, year] = date.split('.');
		const today = new Date();

		return (
			parseInt(day, 10) === today.getDate() &&
			parseInt(month, 10) === today.getMonth() + 1 &&
			parseInt(year, 10) === today.getFullYear()
		);
	};
}

export default Dates;
